import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Package } from '../../models/Package';
import api from '../../api/axios';
import { SellPackageResponse } from '../../models/SellPackageResponse';
import { SellPackageRequest } from '../../models/SellPackageRequest';


interface PackagesState {
  packages: Package[];
  loading: boolean;
  error: string | null;
}

const initialState: PackagesState = {
  packages: [],
  loading: false,
  error: null,
};

export const fetchPackages = createAsyncThunk<Package[]>(
  'packages/fetchPackages',
  async (_, thunkAPI) => {
    try {
      const response = await api.get('pos/Package/getAllPackages');
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.displayMessage || error.response?.data?.message || 'Failed to load packages');
    }
  }
);
export const syncPackages = createAsyncThunk<string>(
  'packages/syncPackages',
  async (_, thunkAPI) => {
    try {
      const response = await api.get('pos/Package/syncAllPackages');
      thunkAPI.dispatch(fetchPackages());
      return response.data.message || 'Packages synchronized successfully.';
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.displayMessage || error.response?.data?.message || 'Failed to sync packages');
    }
  }
);


interface PackageAssignment {
  packageId: string;
  adjustedValue: number;
  resellerDiscountPercent?: number;
}

export const assignPackagesToReseller = createAsyncThunk<
  void, // No data returned
  { resellerId: number; packages: PackageAssignment[] }
>(
  'packages/assignPackagesToReseller',
  async ({ resellerId, packages }, thunkAPI) => {
    try {
      await api.post('/pos/Package/assignPackagesToReseller', {
        resellerId,
        packages,
      });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.displayMessage || error.response?.data?.message || 'Failed to assign packages');
    }
  }
);
export const getAllPackagesByResellerId = createAsyncThunk<Package[]>(
  'packages/getAllPackagesByResellerId',
  async (_, thunkAPI) => {
    try {
      const response = await api.get(`/pos/Package/getAllPackagesByResellerId`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.displayMessage || error.response?.data?.message || 'Failed to load assigned packages');
    }
  }
);

export const fetchAssignedPackages = createAsyncThunk<
  Package[], 
  number
>(
  'packages/fetchAssignedPackages',
  async (resellerId, thunkAPI) => {
    try {
      const response = await api.get(`/pos/Package/admin/getAllPackagesByResellerId?resellerId=${resellerId}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.displayMessage || error.response?.data?.message || 'Failed to load assigned packages');
    }
  }
);
export const sellPackage = createAsyncThunk<SellPackageResponse, SellPackageRequest>(
  'sales/sellPackage',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post('/pos/Resellers/esim/assingPackage', payload);
      return response.data as SellPackageResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.displayMessage || error.response?.data?.message || 'Failed to assign package');
    }
  }
);
const packagesSlice = createSlice({
  name: 'packages',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action: PayloadAction<Package[]>) => {
        state.loading = false;
        state.packages = action.payload;
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(assignPackagesToReseller.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(fetchAssignedPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllPackagesByResellerId.fulfilled, (state, action: PayloadAction<Package[]>) => {
        state.loading = false;
        state.packages = action.payload;
      })
      .addCase(getAllPackagesByResellerId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(sellPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sellPackage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sellPackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default packagesSlice.reducer;
